import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RouteLoadingFallback from '../infrastructure/RouteLoadingFallback.js';
import { canAccessPremiumFeatures } from '../infrastructure/paywallConfig.js';
import { navigateToLearnMoreRenew } from './learnMoreNavigation.js';
import { usePaywall } from './PaywallProvider.js';

/**
 * Gate premium-only features (review cards, The Midway).
 * Requires sign-in and full access (trial, active, teacher, or grandfathered).
 */
export default function PremiumAccessGate({
  isAuthenticated,
  authChecked,
  userId,
  feature = 'premium',
  children,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { access, accessLoading, openSignInModal } = usePaywall();
  const allowed = isAuthenticated && canAccessPremiumFeatures(access);

  useEffect(() => {
    if (!authChecked || accessLoading || allowed) {
      return;
    }

    if (!isAuthenticated) {
      openSignInModal({ reason: `${feature}-locked`, feature });
      return;
    }

    if (access?.tier === 'expired') {
      navigateToLearnMoreRenew(navigate, location);
    }
  }, [
    access?.tier,
    accessLoading,
    allowed,
    authChecked,
    feature,
    isAuthenticated,
    location,
    navigate,
    openSignInModal,
  ]);

  if (!authChecked || accessLoading) {
    return <RouteLoadingFallback />;
  }

  if (!allowed) {
    return null;
  }

  return (
    <>
      {React.Children.map(children, (child) => (
        React.isValidElement(child)
          ? React.cloneElement(child, { userId })
          : child
      ))}
    </>
  );
}
